/**
 * Фича «Подложка»: фото или скан плана квартиры под чертежом, по которому обводят стены.
 *
 * Картинку ужимаем перед тем, как положить в проект: проект хранит её в себе (data URL)
 * и сохраняется целиком — в браузер и на сервер. Длинная сторона — до `MAX_SIDE` пикселей:
 * на скане БТИ этого хватает, чтобы читались стены и подписи, а проект не раздувается.
 * Прозрачный PNG кладём на белое: JPEG прозрачности не знает.
 *
 * Пока масштаб не задан, у картинки предварительный (`UNCALIBRATED_MM_PER_PIXEL`) — сразу
 * после загрузки включается инструмент масштаба, и его задают по известной стене.
 */
import type { Pt } from "@/shared/lib/geometry";
import type { UnderlayImage, WallGraph } from "@/shared/plan";

/** Длинная сторона ужатой картинки, пикселей. */
const MAX_SIDE = 2000;
/** Качество JPEG: линии плана остаются чёткими, файл — в разы меньше PNG. */
const JPEG_QUALITY = 0.85;

/**
 * Масштаб, пока его не задали, мм на пиксель: скан плана двухкомнатной квартиры в 2000 px
 * шириной выходит около 10 м — порядок верный, а точный масштаб задают по стене.
 */
export const UNCALIBRATED_MM_PER_PIXEL = 5;

/** Ошибка открытия картинки — с текстом, который можно показать пользователю. */
export class UnderlayFileError extends Error {}

/** Прочитать файл картинки и ужать её для проекта. */
export const readUnderlayImage = async (file: File): Promise<UnderlayImage> => {
	if (!file.type.startsWith("image/")) {
		throw new UnderlayFileError("Нужна картинка: JPG, PNG или WEBP. PDF сохраните как картинку.");
	}
	let bitmap: ImageBitmap;
	try {
		bitmap = await createImageBitmap(file);
	} catch {
		throw new UnderlayFileError("Не получилось открыть картинку — попробуйте другой файл.");
	}
	const k = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
	const width = Math.round(bitmap.width * k);
	const height = Math.round(bitmap.height * k);
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext("2d");
	if (!context) {
		bitmap.close();
		throw new UnderlayFileError("Браузер не дал обработать картинку — попробуйте ещё раз.");
	}
	context.fillStyle = "#ffffff";
	context.fillRect(0, 0, width, height);
	context.drawImage(bitmap, 0, 0, width, height);
	bitmap.close();
	return { src: canvas.toDataURL("image/jpeg", JPEG_QUALITY), width, height };
};

/**
 * Куда положить новую подложку: если стены уже есть — центром на центр чертежа,
 * чтобы совмещать было недалеко; иначе — левым верхним углом в начало координат.
 */
export const underlayPlacement = (image: UnderlayImage, graph: WallGraph): Pt => {
	const nodes = Object.values(graph.nodes);
	if (nodes.length === 0) return { x: 0, y: 0 };
	const xs = nodes.map((node) => node.x);
	const ys = nodes.map((node) => node.y);
	const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
	const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
	return {
		x: cx - (image.width * UNCALIBRATED_MM_PER_PIXEL) / 2,
		y: cy - (image.height * UNCALIBRATED_MM_PER_PIXEL) / 2,
	};
};

/** Прямоугольник подложки на плане, мм. */
export const underlayBounds = (layer: {
	image: UnderlayImage | null;
	x: number;
	y: number;
	mmPerPixel: number;
}) =>
	layer.image
		? {
				minX: layer.x,
				minY: layer.y,
				maxX: layer.x + layer.image.width * layer.mmPerPixel,
				maxY: layer.y + layer.image.height * layer.mmPerPixel,
			}
		: null;
